import * as React from "react";
import { CardActions, CardContent, CardHeader, Grid, Typography } from "@material-ui/core";
import { Card } from "react-bootstrap";
import { Skeleton } from "@material-ui/lab";
import { useTranslation } from 'react-i18next';
import { IFormOrganism } from "../IFormOrganism";
import { IData } from "../../../store/automatictask/automaticTaskItem";
import { AutomaticTaskIcon } from "../../atoms/icons/AutomaticTaskIcon";
import { TextInput } from "../../molecules/forms/TextInput";
import { NumberInput } from "../../molecules/forms/NumberInput";
import { BooleanInput } from "../../molecules/forms/BooleanInput";
import { FileStatusSelect } from "../../molecules/forms/FileStatusSelect";
import { IData as FileStatusData } from "../../../store/filestatus/filestatusList";

// All the incoming values
interface IProps {
}

// Outgoing functions
interface IDispatch {
    onDirtyChange?: () => void;
}

interface IValidationResult {
    title: boolean;
    description: boolean;
    completionPeriod: boolean;
    forAdmin: boolean;
    mandatory: boolean;
    fileStatusId: boolean;
}

// Actual Component
export type AllProps = IProps & IDispatch & IFormOrganism<IData>;
export const AutomaticTaskEdit = (props: AllProps) => {
    const { t: tAutomaticTask } = useTranslation('automatictask');
    const { t: tDatatypes } = useTranslation('datatypes');
    const [data, setData] = React.useState(props.data);
    const defaultValidationResult = !props.prevalidate;
    const [validationResult, setValidationResult] = React.useState<IValidationResult>({
        title: defaultValidationResult,
        description: defaultValidationResult, // Not required, so false value is valid
        completionPeriod: defaultValidationResult,
        forAdmin: true, // Boolean fields are always valid regardless of their value
        mandatory: true, // Boolean fields are always valid regardless of their value
        fileStatusId: defaultValidationResult
    });
    const loading = props.loading;
    const disabled = props.disabled;
    const hasMounted = React.useRef(false);

    // Sync local data state with props.data when it changes
    React.useEffect(() => {
        setData(props.data);
    }, [props.data]);

    // Check if the form is valid overall
    const isFormValid = Object.values(validationResult).every(valid => valid);

    // Call onUpdate whenever data or validation changes, but not during initial mount/prevalidation
    React.useEffect(() => {
        if (hasMounted.current && props.onUpdate) {
            props.onUpdate(data, isFormValid);
        }
    }, [data, isFormValid]);

    // Track when component has fully mounted
    React.useEffect(() => {
        hasMounted.current = true;
        // Call onUpdate once after mount is complete
        if (props.onUpdate) {
            props.onUpdate(data, isFormValid);
        }
    }, []);

    const updateField = <K extends keyof IData>(field: K, value: IData[K], isValid: boolean = true) => {
        setData(prev => ({ ...prev, [field]: value }));
        setValidationResult(prev => ({ ...prev, [field]: isValid }));
        props.onDirtyChange?.();
    };

    const handleFileStatusChange = (fileStatus: FileStatusData | null, isValid: boolean) => {
        updateField('fileStatusId', fileStatus?.streamId || '', isValid);
    };

    return (
        <Card>
            {loading ? 
                <CardHeader 
                    title={<Skeleton variant="text" />} 
                    subheader={<Skeleton variant="text" />} 
                    avatar={<Skeleton variant="circle" />} 
                /> : 
                <CardHeader 
                    title={tAutomaticTask('editTitle', { title: data.title })} 
                    subheader={tAutomaticTask('editSubtitle')} 
                    avatar={<AutomaticTaskIcon />} 
                />
            }
            <CardContent>
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={3}>
                    
                    {/* Basic Information */}
                    <Grid item xs={12} md={4}>
                        <Typography gutterBottom variant="h5" component="h2">
                            {tAutomaticTask('basisInformatieTitle')}
                        </Typography>

                        <TextInput
                            label={tDatatypes('titleLabel')}
                            value={data.title}
                            loading={loading}
                            disabled={disabled}
                            required={true}
                            onChange={(value, isValid) => updateField('title', value || '', isValid)}
                            prevalidate={props.prevalidate}
                        />

                        <TextInput
                            label={tDatatypes('beschrijvingLabel')}
                            value={data.description}
                            loading={loading}
                            disabled={disabled}
                            multiline={true}
                            rows={4}
                            onChange={(value, isValid) => updateField('description', value || '', isValid)}
                            prevalidate={props.prevalidate}
                        />
                    </Grid>

                    {/* Timing */}
                    <Grid item xs={12} md={4}>
                        <Typography gutterBottom variant="h5" component="h2">
                            Timing
                        </Typography>

                        <FileStatusSelect
                            selectedFileStatusId={data.fileStatusId}
                            loading={loading}
                            disabled={disabled}
                            required={true}
                            label={tDatatypes('triggerStatusLabel')}
                            onChange={handleFileStatusChange}
                            prevalidate={props.prevalidate}
                        />

                        <NumberInput
                            label={tDatatypes('aantalDagenLabel')}
                            value={data.completionPeriod}
                            loading={loading}  
                            disabled={disabled}
                            required={true}
                            minValue={1}
                            maxValue={365}
                            onChange={(value, isValid) => updateField('completionPeriod', value || 0, isValid)}
                            prevalidate={props.prevalidate}
                        />
                    </Grid>

                    {/* Settings */}
                    <Grid item xs={12} md={4}>
                        <Typography gutterBottom variant="h5" component="h2">
                            {tAutomaticTask('instellingenTitle')}
                        </Typography>

                        <BooleanInput
                            label={tDatatypes('adminLabel')}
                            value={data.forAdmin}
                            loading={loading}
                            disabled={disabled}
                            onChange={(value, isValid) => updateField('forAdmin', value ?? false, isValid)}
                        />

                        <BooleanInput
                            label={tDatatypes('verplichtLabel')}
                            value={data.mandatory}
                            loading={loading}
                            disabled={disabled}
                            onChange={(value, isValid) => updateField('mandatory', value ?? false, isValid)}
                        />
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions>
                {props.actions}
            </CardActions>
        </Card>
    );
};
